import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import {
    ButtonPrimary,
    ButtonTertiary,
    ButtonWorkspaceUserDropdown,
    UserContext,
} from "@nova/component-btn"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "@nova/use-translation"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import {
    activateLicenses,
    updateLicense,
    verifyActivationKey,
} from "features/entity-caching/licenses/thunks"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import * as Icons from "@nova/component-icons"
import { WorkspaceCollaboratorProfile } from "../../workspace/body/settings-and-collaborators/collaborators/WorkspaceCollaboratorProfile"
import { selectAllMembers } from "features/entity-caching/members/UserManagementSlice"
import { LicenseModel } from "@nova/db-table-licenses"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { formatSubscriptionType } from "../module"
import { ID } from "@nova/constants"
import { validateEmailAddress } from "@nova/util-strings"
import { hideGlobalModal } from "runlevel/actions"
import { DiscriminatedUnion } from "@nova/util-ts"
import { WorkspaceMember } from "@nova/schemas"

export const ActivateKeyModal = () => {
    const dispatch = useAppDispatch()
    const { t } = useTranslation()

    const [keyInput, setKeyInput] = useState("")
    const [mode, setMode] = useState<"activate" | "apply" | "assign">("activate")
    const [stagedUser, setStagedUser] = useState<UserContext | undefined>(undefined)

    const [activatedLicenses, setActivatedLicenses] = useState<{
        licenses: LicenseModel[]
        addOns: LicenseModel[]
    } | null>(null)
    const [stagedAssignments, setStagedAssignments] = useState<
        Record<ID, { sub: LicenseModel; user?: UserContext }>
    >({})

    const isAnyEmailInvalid = useMemo(() => {
        if (mode !== "assign") return false
        return Object.values(stagedAssignments).some((assignment) => {
            return (
                assignment.user &&
                !!assignment?.user?.email &&
                !validateEmailAddress(assignment?.user?.email)
            )
        })
    }, [stagedAssignments, mode])

    const activeLicensesWithKey = useCallback(async () => {
        if (!keyInput) return
        const result = await dispatch(verifyActivationKey({ activationKey: keyInput })).unwrap()
        if (!result) {
            dispatch(
                setGlobalNotificationContext({
                    message: "Invalid code, please try again.",
                    status: GlobalNotificationStatus.error,
                    timeoutInMs: 3000,
                })
            )
            return
        }
        setMode("apply")
    }, [keyInput, dispatch])

    const applyActivatedSubscriptions = useCallback(async () => {
        if (!stagedUser || !keyInput) return

        const activatedLicenses = await dispatch(
            activateLicenses({
                ownerId: stagedUser.userId || stagedUser.fullName || "",
                activationKey: keyInput,
            })
        ).unwrap()

        setActivatedLicenses(activatedLicenses)
        setMode("assign")
    }, [stagedUser, keyInput, dispatch])

    const assignActivatedSubscriptions = useCallback(async () => {
        await Promise.all(
            Object.values(stagedAssignments).map((assignment) =>
                dispatch(
                    updateLicense({
                        ownerId: assignment.sub.ownerId,
                        licenseId: assignment.sub.licenseId,
                        attributes: {
                            assigneeId:
                                assignment.user?.userId ||
                                assignment.user?.email ||
                                assignment.user?.fullName ||
                                "unassigned",
                        },
                    })
                ).catch()
            )
        )

        dispatch(
            setGlobalNotificationContext({
                message: "Key activated and assigned successfully.",
                status: GlobalNotificationStatus.success,
                timeoutInMs: 3000,
            })
        )
        dispatch(hideGlobalModal())
    }, [stagedAssignments, dispatch])

    const allMembers = useAppSelector(selectAllMembers())
    const dropdownButtons = useMemo(() => {
        const allMembersArray = Object.values(
            allMembers
        ).flat() as DiscriminatedUnion<WorkspaceMember>[]
        const seen = new Set<string>()
        const uniqueMembers = allMembersArray.filter((item) => {
            if (seen.has(item.userId)) {
                return false
            }
            seen.add(item.userId)
            return true
        })
        return uniqueMembers.map((member) => {
            return {
                userId: member.userId,
                fullName: member.fullName,
                email: member.email || member.userId,
                CollaboratorComponent: (
                    <WorkspaceCollaboratorProfile
                        userId={member.userId}
                        email={member.email}
                        fullName={member.fullName}
                    />
                ),
            }
        })
    }, [allMembers])

    const title = mode === "activate" ? "Activate" : mode === "apply" ? "Apply" : "Assign"
    return (
        <>
            <HighPriorityNovaModalContainer
                title="Activate key"
                icon={Icons.Key}
                className="w-[30rem]"
                bottomBar={
                    <div className="w-full flex justify-end items-center gap-2">
                        <ButtonTertiary
                            title="Cancel"
                            onClick={() => dispatch(hideGlobalModal())}
                        />
                        <ButtonPrimary
                            title={title}
                            onClick={
                                mode === "activate"
                                    ? activeLicensesWithKey
                                    : mode === "apply"
                                      ? applyActivatedSubscriptions
                                      : assignActivatedSubscriptions
                            }
                            disabled={
                                mode === "activate"
                                    ? !keyInput
                                    : mode === "apply"
                                      ? !stagedUser
                                      : isAnyEmailInvalid
                            }
                        />
                    </div>
                }
            >
                {mode === "activate" && (
                    <>
                        <h5 className="mb-2.5">
                            {t("Enter your key to apply licenses or add-ons to an account.")}
                        </h5>
                        <h5 className="text-gray-100 mb-2">{t("Key")}</h5>
                        <WorkspaceTextInput
                            name="activate-key"
                            value={keyInput}
                            handleChange={setKeyInput}
                            placeholder="Enter your key"
                        />
                    </>
                )}
                {mode === "apply" && (
                    <>
                        <h5 className="mb-2.5">
                            {t(
                                "Provide an email to activate the purchase for, they will be the subscriptions owner"
                            )}
                        </h5>
                        <h5 className="text-gray-100 mb-2">{t("Email")}</h5>
                        <ButtonWorkspaceUserDropdown
                            userDropdownButtons={dropdownButtons}
                            widthClass="w-[25.75rem]"
                            setSelectedUser={(user) => {
                                setStagedUser(user)
                            }}
                        />
                        {stagedUser && (
                            <div className="max-h-[18.438rem] flex flex-col gap-4 mt-2 p-6 py-4 border rounded-md border-gray-700">
                                <WorkspaceCollaboratorProfile
                                    userId={stagedUser.userId}
                                    email={stagedUser.email}
                                    fullName={stagedUser.fullName}
                                />
                            </div>
                        )}
                    </>
                )}
                {mode === "assign" && (
                    <>
                        <h5 className="mb-2.5">
                            {t(
                                `You have activated these licenses and add ons for ${stagedUser?.email || stagedUser?.fullName}. Assign them to emails or leave them unassigned.`
                            )}
                        </h5>
                        <ScrollableArea className="max-h-[300px]">
                            <h5 className="text-gray-100 mb-2">{t("Licenses")}</h5>
                            <div className="flex flex-col gap-2">
                                {activatedLicenses?.licenses
                                    .sort((l1, l2) =>
                                        l1.subscriptionType.localeCompare(l2.subscriptionType)
                                    )
                                    .map((license, idx) => {
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-center justify-between gap-3 mr-3"
                                            >
                                                <h5 className="capitalize">
                                                    {license.subscriptionType}
                                                </h5>
                                                <ButtonWorkspaceUserDropdown
                                                    userDropdownButtons={dropdownButtons}
                                                    widthClass="w-[18rem]"
                                                    setSelectedUser={(user) => {
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[license.licenseId] = {
                                                                sub: license,
                                                                user,
                                                            }
                                                            return newAssignments
                                                        })
                                                    }}
                                                    handleChange={(email) => {
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[license.licenseId] = {
                                                                sub: license,
                                                                user: { email },
                                                            }
                                                            return newAssignments
                                                        })
                                                    }}
                                                    onClear={() =>
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[license.licenseId] = {
                                                                sub: license,
                                                                user: undefined,
                                                            }
                                                            return newAssignments
                                                        })
                                                    }
                                                    portalPositioning={{
                                                        portalId: "active-key-dropdown-portal",
                                                        relativeOffset: 40,
                                                    }}
                                                    displaySelection
                                                />
                                            </div>
                                        )
                                    })}
                            </div>
                            <h5 className="text-gray-100 mt-4 mb-2">{t("Add-ons")}</h5>
                            <div className="flex flex-col gap-2">
                                {activatedLicenses?.addOns
                                    .sort((l1, l2) =>
                                        l1.subscriptionType.localeCompare(l2.subscriptionType)
                                    )
                                    .map((addOn, idx) => {
                                        return (
                                            <div
                                                key={idx}
                                                className="flex items-center justify-between gap-3 mr-3"
                                            >
                                                <h5 className="capitalize">
                                                    {formatSubscriptionType(addOn.subscriptionType)}
                                                </h5>
                                                <ButtonWorkspaceUserDropdown
                                                    userDropdownButtons={dropdownButtons}
                                                    widthClass="w-[18rem]"
                                                    setSelectedUser={(user) => {
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[addOn.licenseId] = {
                                                                sub: addOn,
                                                                user,
                                                            }
                                                            return newAssignments
                                                        })
                                                    }}
                                                    handleChange={(email) => {
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[addOn.licenseId] = {
                                                                sub: addOn,
                                                                user: { email },
                                                            }
                                                            return newAssignments
                                                        })
                                                    }}
                                                    onClear={() =>
                                                        setStagedAssignments((assignments) => {
                                                            const newAssignments = {
                                                                ...assignments,
                                                            }
                                                            newAssignments[addOn.licenseId] = {
                                                                sub: addOn,
                                                                user: undefined,
                                                            }
                                                            return newAssignments
                                                        })
                                                    }
                                                    portalPositioning={{
                                                        portalId: "active-key-dropdown-portal",
                                                        relativeOffset: 40,
                                                    }}
                                                    displaySelection
                                                />
                                            </div>
                                        )
                                    })}
                            </div>
                        </ScrollableArea>
                        {isAnyEmailInvalid && (
                            <div className="text-red-500 flex items-center gap-1 mt-2">
                                <Icons.Error />
                                <h5>{t("One of the emails is an invalid format")}</h5>
                            </div>
                        )}
                    </>
                )}
            </HighPriorityNovaModalContainer>
            <div id="active-key-dropdown-portal" className="w-full" />
        </>
    )
}
